import React, { useEffect, useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import RpqsAction from 'rpqs/actions/RpqsAction'
import { push } from '@lagunovsky/redux-react-router'
import useActions from 'utils/customHook/useActions'
import { PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY_NEW } from 'home/constants/RouteConstants'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import SimpleInput from 'components/forms/SimpleInput'
import Select from 'components/forms/Select'

const MANAGEMENT_ENTITIES_HEADERS = ['code', 'name', 'nbCities', 'nbRPQS', 'lastRPQS']

const ManagementEntitiesApp = () => {
    const {
        managementUnits,
    } = useSelector(store => ({
        managementUnits: store.RpqsReducer.managementUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(RpqsAction.fetchAllManagementUnits())
    }, [])

    const [filter, setFilter] = useState({})
    const [appliedFilter, setAppliedFilter] = useState({})
    const [managementUnitsWithLinks, setManagementUnitsWithLinks] = useState([])

    useTitle(() => [{
        title: i18n.rpqs,
        href: 'rpqs',
    }, {
        title: i18n.RPQSReferencials,
        href: 'rpqs/referencials',
    }, {
        title: i18n.managementEntities,
        href: 'rpqs/referencials/managementEntities',
    }], [])

    const managementEntitiesSelect = useMemo(() => managementUnits.map((e) => (
        {
            ...e,
            codeWithLabel: `${e.name} [${e.code}]`,
        }
    )), [managementUnits])

    useEffect(() => {
        Promise.all(managementUnits
            .filter(m => m?.id)
            .map((m) => dispatch(RpqsAction.getManagementUnitById(m.id))),
        ).then(setManagementUnitsWithLinks)
    }, [managementUnits])

    const filteredData = useMemo(() => {
        const filteredSearchValue = appliedFilter.searchValue
            ? managementUnitsWithLinks.filter(e => `${e.code} ${e.name}`.toLowerCase().includes(appliedFilter.searchValue.toLowerCase()))
            : managementUnitsWithLinks

        const filteredManagementEntity = appliedFilter.managementEntity
            ? filteredSearchValue.filter(e => e.code === appliedFilter.managementEntity)
            : filteredSearchValue

        return filteredManagementEntity
    }, [appliedFilter, managementUnitsWithLinks])

    useActions(() => {
        return {
            new: () => dispatch(push(PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(filteredData, MANAGEMENT_ENTITIES_HEADERS),
                        titleFile: i18n.listManagementEntities,
                    },
                },
            },
        }
    }, [filteredData])

    const data = useMemo(() => filteredData
        .filter(m => m?.id)
        .map(m => (
            {
                id: m.id,
                code: { value: m.code },
                name: { value: m.name },
                nbCities: { value: m.link_cities?.length ?? 0 },
            }
        )), [filteredData])

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <SimpleInput
                                title={i18n.search}
                                value={filter.searchValue}
                                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
                                onEnterKeyPress={() => setAppliedFilter(filter)}
                                data-cy='search_input'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                label={i18n.managementEntities}
                                options={managementEntitiesSelect}
                                value={filter.managementEntity}
                                keyLabel={'codeWithLabel'}
                                onChange={managementEntity => setFilter(prev => ({ ...prev, managementEntity }))}
                            />
                        </Grid>
                        <Grid container size={12} spacing={1} justifyContent='flex-end'>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setFilter({})
                                    setAppliedFilter({})
                                }}
                            >
                                {i18n.reinit}
                            </Button>
                            <Button
                                variant='contained'
                                onClick={() => setAppliedFilter(filter)}
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.listManagementEntities}
                rows={data}
                headers={MANAGEMENT_ENTITIES_HEADERS}
                onClickRow={me => dispatch(push(`/rpqs/referencials/managementEntities/${me.id}/dashboard`))}
            />
        </Stack>
    )
}

export default ManagementEntitiesApp
